<template>
	<div>
		<div
			v-if="loading"
			class="w-full flex justify-center items-center py-80"
			style="margin-top: calc(7px - 100px)"
		>
			<spinner :small="true" />
		</div>

		<template v-else>
			<section class="pt-0 bg-gray50 px-20 md:px-90">
				<div
					class="relative w-full mx-auto shadow z-5"
					style="top: -350px; margin-bottom: -400px; max-width: 1030px;"
				>
					<div
						class="rounded-t bg-gray800 relative"
						style="{ height: listing.photos.length > 0 ? '400px' : '' }"
					>
						<div class="listing-item-type z-5" style="top: -41px;">
							<div class="absolute pin opacity-40 rounded-t" :class="listingColor.replace('text-', 'bg-')"></div>
							<div class="relative">
								<ListingIcon :icon="listingIcon" size="s" />
								{{ getPropertyLabel(listing) }}
							</div>
						</div>

						<carousel
							style="padding-top: 0px; margin-bottom: 0px;"
							:per-page="perPage"
							:pagination-enabled="false"
							:navigation-enabled="true"
							:loop="true"
							:mouse-drag="false"
							navigation-next-label
							navigation-prev-label
							v-if="listing.photos.length > 0"
						>
							<slide class="flex" style="height: 400px">
								<div
									class="w-full h-full bg-cover bg-center"
									:style="`background-image: url(${listing.photos[0].thumb})`"
									@click="openGallery(0)"
								></div>
							</slide>
							<template v-if="photos.length">
								<slide class="flex" style="height: 400px" v-for="i in galleryRange" :key="'range-'+i">
									<div class="flex flex-row w-full h-full flex-wrap">
										<template v-for="(photo, index) in photos">
											<a
												v-if="photos[index + (i * 4)] && (photos[index + (i * 4)].thumb == listing.photos[0].thumb)"
												class="bg-cover bg-center flex flex-no-grow cursor-pointer"
												:class="getSlideSize(index, i)"
												:style="`background-image: url(${listing.photos[0].thumb});`"
												:key="index + (i * 4)"
												@click="openVideo"
												target="_blank"
											>
												<div
													class="h-full flex flex-col flex-grow p-15 items-center justify-center video relative"
												>
													<div class="absolute pin opacity-80" :class="listingColor.replace('text', 'bg')"></div>
													<span class="icon-m-play-circle"></span>
													<div class="p-20 relative">Video Tour</div>
												</div>
											</a>
											<div
												v-else-if="photos[index + (i * 4)]"
												class="bg-cover bg-center"
												:class="getSlideSize(index, i)"
												:style="`background-image: url(${photos[index + (i * 4)].thumb})`"
												:key="index + (i * 4)"
												@click="openGallery(index + (i * 4) + 1)"
											></div>
										</template>
									</div>
								</slide>
							</template>
						</carousel>
					</div>

					<div class="flex flex-wrap bg-white rounded-b">
						<ul class="p-45 md:p-70 w-full lg:w-2/3">
							<div
								v-if="listing.package && listing.settings.inPackageOnly"
								class="flex bg-gray50 py-20 pr-20 rounded"
							>
								<div class="bg-white ml-2 -my-18 flex items-center">
									<ListingIcon :color="listingColor" :icon="listingIcon" class="m-25 block" />
								</div>
								<div class="flex flex-wrap items-center flex-grow ml-20 -my-8">
									<div class="flex flex-col flex-grow mr-20 my-8">
										<div
											class="font-secondary font-bold text-tiny text-gray700 uppercase tracking-loose"
										>In {{ listing.package.type.label }} Only</div>
										<div class="flex items-center mt-10 -ml-4">
											<ListingIcon :color="types['package'].color" icon="package-only" />
											<div
												class="ml-10 font-secondary font-bold text-xl text-gray700"
												v-html="getPackagePrice()"
											></div>
										</div>
									</div>
									<a
										class="filled-button block text-center py-20 my-8"
										:href="listing.package.url"
										:class="'filled-button-' + listingColor.slice(5, -3)"
									>
										<span
											class="filled-button-content"
										>View {{ listing.package.type.label.replace('Combined ', '') }}</span>
										<span class="button-arrow filled-button-arrow"></span>
									</a>
								</div>
							</div>

							<div class="flex" v-else-if="!listing.settings.inPackageOnly">
								<ListingIcon :color="listingColor" :icon="listingIcon" />
								<div class="ml-20 font-secondary font-bold text-xl text-gray700" v-html="getPrice()"></div>
								<div
									v-if="listing.settings.buildToSuit"
									class="ml-15 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8 self-start flex-no-shrink"
								>Build To Suit</div>
							</div>

							<div class="text-gray700 leading-loose mt-40" v-html="description"></div>

							<template v-if="listing.features.length">
								<div
									class="mt-40 mb-15 font-secondary font-bold text-xs text-gray400 uppercase tracking-loose"
								>Features</div>

								<ul class="features">
									<li v-for="(feature, index) in listing.features" :key="index">{{ feature }}</li>
								</ul>
							</template>

							<template v-if="listing.attachments.length">
								<div
									class="mt-40 mb-15 font-secondary font-bold text-xs text-gray400 uppercase tracking-loose"
								>Attachments</div>

								<div class="-m-10">
									<a
										v-for="(attachment, index) in listing.attachments"
										class="outlined-button border-gray100 text-center m-10"
										target="_blank"
										:key="index"
										:href="attachment.url"
										:class="listingColor"
									>
										<span class="outlined-button-content">{{ attachment.title }}</span>
										<span class="button-arrow outlined-button-arrow"></span>
									</a>
								</div>
							</template>
						</ul>

						<div
							class="w-full lg:w-1/3 flex-no-shrink flex flex-col border-solid border-gray100 font-secondary font-semibold text-sm border-t lg:border-t-0 lg:border-l"
						>
							<div
								class="px-50 py-40 border-b border-solid border-gray100"
								v-if="listing.usage.best.length"
							>
								<div
									class="mb-10 font-secondary font-bold text-xs text-gray400 uppercase tracking-loose"
								>Best Use</div>
								<div class="ts-color text-gray700">{{ listing.usage.best.join(', ') }}</div>
							</div>
							<div
								class="flex py-40 px-45 md:px-50 border-b border-solid border-gray100"
								v-if="listing.sf && listing.sf.min > 0 && listing.sf.max > 0 || listing.acres > 0"
							>
								<ul class="flex flex-col justify-around">
									<li
										v-if="listing.sf && listing.sf.min > 0 && listing.sf.max > 0"
										class="flex items-center py-15"
									>
										<div class="flex justify-center w-6 mr-20 icon-m-measure text-gray300"></div>
										<div class="text-gray700">{{ getSf(listing) }}</div>
									</li>
									<li v-if="listing.acres > 0" class="flex items-center py-15">
										<div class="flex justify-center w-6 mr-20 icon-m-tree text-gray300"></div>
										<div class="text-gray700">{{ getAcres(listing) }}</div>
									</li>
								</ul>
							</div>
							<div class="flex-grow px-50 py-40" v-if="listing.city || listing.county">
								<div
									v-if="listing.city"
									class="mb-15 font-secondary font-bold text-xs text-gray400 uppercase tracking-loose"
								>{{ listing.city.length == 1 ? 'City' : 'Cities' }}</div>
								<a
									v-for="(city, index) in listing.city"
									class="group block flex items-center mt-10"
									:href="city.url"
									:key="index"
								>
									<img v-if="city.image" :src="city.image" :alt="'Photo of ' + city.name" class="h-6 w-6 mr-15 rounded-full" />
									<div v-else class="h-6 w-6 mr-15 rounded-full bg-gray50"></div>
									<div class="ts-color text-gray700 group-hover:text-gray900 pb-2">{{ city.name }}</div>
								</a>
								<div
									v-if="listing.county"
									class="mb-15 font-secondary font-bold text-xs text-gray400 uppercase tracking-loose"
									:class="listing.city ? 'mt-30' : ''"
								>{{ listing.county.length == 1 ? 'County' : 'Counties' }}</div>
								<a
									v-for="(county, index) in listing.county"
									class="group block flex items-center mt-10"
									:href="county.url"
									:key="county.index"
								>
									<img v-if="county.image" :src="county.image" :alt="'Photo of ' + county.name" class="h-6 w-6 mr-15 rounded-full" />
									<div v-else class="h-6 w-6 mr-15 rounded-full bg-gray50"></div>
									<div class="ts-color text-gray700 group-hover:text-gray900 pb-2">{{ county.name }}</div>
								</a>
							</div>
							<template v-if="listing.package && !listing.settings.inPackageOnly">
								<a :href="listing.package.url" class="flex items-center bg-gray50 py-18 pl-20 pr-25">
									<ListingIcon :color="types['package'].color" icon="package-included" />
									<div
										class="ml-15 mr-auto font-secondary font-bold text-xs text-gray500 uppercase tracking-loose"
									>Offered In {{ listing.package.type.label }}</div>
									<span class="button-arrow filled-button-arrow"></span>
								</a>
							</template>
						</div>
					</div>
				</div>

				<div
					class="relative w-full mx-auto"
					style="margin-top: 140px; max-width: 1030px;"
					v-if="listing.properties"
				>
					<h2 class="text-center">Included Properties</h2>
				</div>

				<div
					class="relative w-full mx-auto shadow z-5"
					style="margin-top: 60px; margin-bottom: -50px; max-width: 1030px;"
					v-if="listing.properties"
				>
					<carousel
						style="padding-top: 0px; margin-bottom: 0px;"
						:per-page="listing.properties.length > 2 ? 3 : 2"
						:pagination-enabled="false"
						:navigation-enabled="true"
						:loop="true"
						:mouse-drag="false"
						navigation-next-label
						navigation-prev-label
					>
						<slide class="flex" v-for="(property, index) in listing.properties" :key="index">
							<div class="listing-item relative flex flex-col w-full">
								<a
									class="listing-item-header cursor-pointer"
									:href="property.url"
									style="z-index: initial;"
								>
									<span
										class="bg-blue200 cursor-pointer absolute pin bg-cover bg-center"
										:style="'background-image: url(' + property.photos[0].thumb + '); z-index: -1;'"
										v-if="property.photos.length"
									></span>
									<div v-else class="absolute pin bg-gray700">
										<div
											class="w-full h-full bg-cover bg-center"
											:style="'background-image: url(\'/assets/img/properties/' + property.propertyType.value + '.jpg\'); z-index: -1; filter: blur(4px);'"
										>
											<div class="w-full h-full" style="background-color: rgba(27, 50, 61, 0.8);"></div>
										</div>
									</div>
								</a>
								<div class="listing-item-details-wrapper flex-grow" style="padding: 0px;">
									<a
										class="listing-item-details cursor-pointer mt-0 rounded-none h-full flex flex-col"
										:href="property.url"
									>
										<div class="listing-item-type" style="top: 0px; left: 30px;">
											<div
												class="absolute pin opacity-40 rounded-b"
												:class="types[property.propertyType.value].color.replace('text-', 'bg-')"
											></div>
											<div class="relative">{{ getPropertyLabel(property) }}</div>
										</div>

										<div class="listing-item-details-top p-40 mt-0 mb-auto">
											<div class="flex items-center" :class="property.propertyType.value">
												<span class="-ml-3 listing-item-icon" :class="iconClasses(property)"></span>
												<ListingIcon
													:color="types['package'].color"
													icon="package-only"
													class="ml-15"
													v-if="property.settings.inPackageOnly"
												/>
												<div
													v-if="property.settings.inPackageOnly && property.package"
													class="ml-20 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8"
												>In {{ property.package.type.label }} Only</div>
												<div
													v-else
													class="ml-15 text-xl text-gray700 font-secondary font-bold"
												>{{ getPropertyPrice(property) }}</div>
											</div>

											<div class="mt-20">
												<div class="font-bold text-gray700">{{ getStreet(property) }}</div>
												<div class="mt-10 text-sm">{{ getCityStateZip(property) }}</div>
											</div>
										</div>
										<div class="border-t border-solid border-gray100">
											<div class="-mx-10 px-40 py-25 flex">
												<div class="flex px-10" v-if="property.sf.min">
													<span class="icon-m-measure text-gray300"></span>
													<div class="listing-info-item ml-10">{{ getSf(property) }}</div>
												</div>
												<div class="flex px-10 acres" v-if="property.acres">
													<span class="icon-m-tree text-gray300"></span>
													<div class="listing-info-item ml-10">{{ getAcres(property) }}</div>
												</div>
											</div>
										</div>
									</a>
								</div>
							</div>
						</slide>
					</carousel>
				</div>
			</section>

			<section class="-mx-5 pt-5 md:pt-10 bg-white flex flex-wrap">
				<div
					class="px-5 pb-5 md:pb-10 lg:pb-0 w-full"
					:class="{'lg:w-3/5': listing.status.value == 'active'}"
					style="min-height: 40vh;"
				>
					<div class="map-wrapper h-full bg-blue200 rounded-l relative">
						<!-- Map -->
						<div id="map" class="w-full h-full" style="font-family: inherit;"></div>
						<div
							v-if="mapUnavailable"
							class="absolute pin flex justify-center items-center z-3 bg-blue200"
						>
							<div class="bg-blue400 p-30 rounded trans text-center text-white text-sm">
								Map view is unavailable right now. Use the directions link for this property's location.
							</div>
						</div>
						<!-- Zoom Controls -->
						<div
							v-if="map"
							class="absolute pin-t pin-r m-20 bg-white rounded mt-60 md:mt-20 lg:mt-50 cursor-pointer"
						>
							<div class>
								<div
									@click="map.zoomTo(map.getZoom() + 1)"
									class="px-12 py-5 h-40 flex items-center justify-center border-b-2"
								>
                  <img src="/assets/img/plus.svg" alt="Zoom In"/>
								</div>
								<div class="w-full h-1 bg-gray100"></div>
								<div
									@click="map.zoomTo(map.getZoom() - 1)"
									class="px-12 py-5 h-40 flex items-center justify-center"
								>
                  <img src="/assets/img/minus.svg" alt="Zoom Out"/>
								</div>
							</div>
						</div>
						<!-- Directions Button -->
						<div class="absolute pin-b pin-r m-20 lg:mb-50 flex">
							<!-- Sat / Map -->
							<div
								v-if="map && mapStyle == 'streets-v9'"
								class="p-10 px-20 bg-white rounded uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer flex items-center"
								@click="setMapStyle('satellite-v9')"
							>Satellite</div>
							<div
								v-else-if="map"
								class="p-10 px-20 bg-white rounded uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer flex items-center"
								@click="setMapStyle('streets-v9')"
							>Map</div>
							<a
								v-if="listing.propertyType.value != 'package'"
								target="_blank"
								:href="'http://maps.google.com/maps/place/' + getStreet(listing) + ' ' + getCityStateZip(listing)"
								class="ml-15"
							>
								<div
									class="p-10 px-20 bg-white rounded flex items-center uppercase tracking-looser text-tiny font-secondary font-bold cursor-pointer flex items-center"
								>
									Get Directions
									<span class="button-arrow filled-button-arrow"></span>
								</div>
							</a>
						</div>
					</div>
					<!-- <div class="h-full bg-blue200 rounded-l"></div> -->
				</div>
				<div class="px-5 w-full lg:w-2/5 flex" v-if="listing.status.value == 'active'">
					<!-- If there's an agent... -->
					<div class="flex justify-center pt-90 pb-60 lg:py-110 px-50 bg-gray700 rounded-r w-full">
						<div class="flex flex-col justify-center">
							<div class="flex flex-col items-center text-center">
								<div class="flex flex-row mb-20" v-if="listing.brokers.length">
									<a v-for="agent in listing.brokers" class="block px-10" :href="agent.url" :key="agent.id">
										<img
											v-if="agent.photo"
											class="h-18 rounded-full ts-shadow shadow hover:shadow-dark"
											:src="agent.photo"
											:alt="agent.name"
										/>
									</a>
								</div>
								<div class="font-secondary font-bold text-lg text-white">{{ agentNames }}</div>
								<div class="mt-10 font-secondary font-bold text-xs text-gray300 uppercase tracking-loose">
									Pickett Sprouse
									<br />Commercial Real Estate
								</div>
							</div>

							<div class="flex flex-col mt-30 max-w-250 mx-auto">
								<a
									class="outlined-button border-gray600 text-center"
									:href="`tel:${showingPhone}`"
									v-if="listing.status.label != 'Closed'"
								>
									<span
										class="outlined-button-content font-primary text-white text-base tracking-normal"
									>{{ showingPhone }}</span>
								</a>

								<LeadCapturing
									:classnames="'mt-20 filled-button block text-center filled-button-' + listingColor.slice(5, -3)"
									v-if="listing.status.label != 'Closed'"
								>
									<span class="filled-button-content">Request A Showing</span>
									<span class="button-arrow filled-button-arrow"></span>
								</LeadCapturing>
							</div>
						</div>
					</div>
				</div>
			</section>
			<section class="bg-gray50 pt-0 py-40 px-20 md:px-90 details">
				<div
					class="relative flex flex-wrap w-full -top-40 -mb-40 mx-auto bg-white shadow rounded z-5"
					style="max-width: 1030px;"
				>
					<div class="w-full lg:w-1/2">
						<ListingInformationSection
							heading="Property Details"
							:headingColor="listingColor"
							:items="propertyDetails"
						/>
					</div>
					<div class="w-full lg:w-1/2 lg:border-l border-solid border-gray100">
						<ListingInformationSection
							heading="Listing Details"
							:headingColor="listingColor"
							:items="listingDetails"
						/>
					</div>
					<div
						class="w-full lg:border-t border-solid border-gray100"
						v-if="listing.status.value == 'closed' && listing.legacy.analysis && listing.legacy.granteeTenant && listing.legacy.grantorLandlord && listing.legacy.noMultifamilyUnits && listing.legacy.useAtTimeOfSale && listing.legacy.utilities"
					>
						<ListingInformationSection
							heading="Legacy Details"
							:headingColor="listingColor"
							:items="legacyDetails"
						/>
					</div>
					<a
						v-if="listing.video"
						class="w-full h-150 bg-cover bg-center flex flex-no-grow cursor-pointer"
						:style="'background-image: url(' + listing.photos[0].thumb + ');'"
						@click="openVideo"
						target="_blank"
					>
						<div class="h-full flex flex-grow p-15 items-center justify-center video relative">
							<div class="absolute pin opacity-80" :class="listingColor.replace('text', 'bg')"></div>
							<span class="icon-m-play-circle"></span>
							<div class="p-20 relative">Video Tour</div>
						</div>
					</a>
				</div>
				<div
					class="relative flex flex-wrap w-full mt-20 mx-auto bg-white shadow rounded z-5"
					style="max-width: 1030px;"
					v-if="listing.notes && listing.status.value == 'closed'"
				>
					<div class="w-full">
						<div class="pt-40 md:pt-50 pb-30 md:pb-40 px-45 md:px-70">
							<h3 :class="listingColor">Notes</h3>

							<div class="mt-20 text-gray700 leading-normal" v-html="this.notes"></div>
						</div>
					</div>
				</div>
				<div class="w-full lg:w-3/4 mx-auto pt-70 pb-40 px-40 md:px-80 text-center">
					<p
						class="text-sm"
					>Information deemed reliable but not guaranteed to be accurate. © Pickett Sprouse Commercial Real Estate. All rights reserved.</p>
				</div>
			</section>

			<transition name="fade">
				<ListingGallery
					v-if="showGallery"
					:photos="listing.photos.map(photo => photo.full)"
					:photoIndex="galleryIndex"
					:windowSize="windowSize"
					@close="closeGallery"
					@prev-photo="prevPhoto"
					@next-photo="nextPhoto"
				/>
			</transition>

			<div v-if="showVideo && listing.video" class="fixed pin flex items-center z-max">
				<div class="absolute pin bg-gray900 opacity-90" @click="showVideo = false"></div>

				<div class="relative w-full flex justify-center items-center px-80">
					<div class="flex flex-col items-center justify-center w-full max-w-3/4" @click.stop>
						<video class="w-full" controls>
							<source :src="listing.video" type="video/mp4" />
						</video>
					</div>
				</div>

				<!-- Close Button -->
				<div class="absolute pin-t pin-r p-30">
					<button class="bg-transparent text-white" @click="showVideo = false" aria-label="Close Video">
            <img src="/assets/img/x.svg" alt="An image that looks like an X, on click it closes the video"/>
					</button>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
	import _ from "lodash";
	import axios from "axios";

	// import helpers
	import stateToAbbreviation from "../../helpers/stateToAbbr.js";
	import formatPrice from "../../helpers/formatPrice.js";
	import addCommas from "../../helpers/addCommas.js";

	// import Vue components
	import ListingIcon from "../properties/listing-icon.vue";
	import ListingInformationSection from "./listing-information-section.vue";
	import ListingGallery from "./listing-gallery.vue";
	import LeadCapturing from '../forms/lead-capturing.vue';

	import Carousel from "../general/carousel.vue";
	import Slide from "../general/carousel-slide.vue";

	import spinner from "../general/spinner.vue";

	// Loads and presents commercial property/package detail data and related records.
	export default {
		props: {
			windowSize: { type: String, default: 'md' },
		},
		data() {
			return {
				listing: {},
				loading: true,
				modified: null,
				showGallery: false,
				showVideo: false,
				galleryIndex: 0,
				mapStyle: "streets-v9",
				map: null,
				mapUnavailable: false,
				types: {
					land: {
						color: "text-green500",
						icon: "land"
					},
					office: {
						color: "text-blue500",
						icon: "commercial"
					},
					retail: {
						color: "text-purple500",
						icon: "retail"
					},
					residential: {
						color: "text-red500",
						icon: "townhouse"
					},
					industrial: {
						color: "text-orange500",
						icon: "industrial"
					},
					specialty: {
						color: "text-yellow500",
						icon: "other"
					},
					healthcare: {
						color: "text-pink500",
						icon: "health"
					},
					hospitality: {
						color: "text-magenta500",
						icon: "condo"
					},
					entertainment: {
						color: "text-teal500",
						icon: "entertainment"
					},
					package: {
						color: "text-gray500",
						icon: "package"
					},
					combinedSpace: {
						color: "text-gray500",
						icon: "package"
					}
				}
			};
		},
		methods: {
			setMapStyle(style) {
				if (!this.map) {
					return;
				}

				this.mapStyle = style;
				this.map.setStyle("mapbox://styles/mapbox/" + style);
			},
			openVideo() {
				if (this.listing.video.includes(".mp4")) {
					this.showVideo = true;
				} else {
					window.open(this.listing.video);
				}
			},
			getListing(id) {
				var vm = this;
				var location =
					window.location.protocol + "//" + window.location.hostname;
				axios.get(location + `${window.location.href.includes('ddev') ? '/commercial' : ''}/properties/${id}.json`).then(response => {
					this.listing = response.data;
					this.loading = false;
					vm.$nextTick(function() {
						vm.createMap();
					});
				});
			},
			iconClasses(listing) {
				var iconClasses = "";
				var propertyType = listing.propertyType.value;

				var type = this.types[propertyType];

				iconClasses += type.color + " ";

				// Define the size and icon
				iconClasses += "icon-m-" + type.icon;

				return iconClasses;
			},
			getPropertyLabel(listing) {
				let propertyType = listing.propertyType.label;
				let listingType = listing.listingType;
				let status = listing.status.label;

				let listingTypeLabel = "";

				if (
					listing.settings &&
					listing.settings.inPackageOnly &&
					listing.package
				) {
					listingTypeLabel = "In " + listing.package.type.label;
				} else {
					if (status == "Closed") {
						listingTypeLabel = "";
						if (listingType.sale) {
							listingTypeLabel = "Sold";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel = "Closed";
						}
						if (listingType.lease) {
							listingTypeLabel = "Leased";
						}
					} else {
						listingTypeLabel = "For ";
						if (listingType.sale) {
							listingTypeLabel += "Sale";
						}
						if (listingType.sale && listingType.lease) {
							listingTypeLabel += " & ";
						}
						if (listingType.lease) {
							listingTypeLabel += "Lease";
						}
						if(status == "Pending"){
							listingTypeLabel += " • Pending";
						}
					}
				}

				// Determine base label
				let propertyLabel = propertyType;

				// Add Listing Type
				propertyLabel += " • " + listingTypeLabel;

				return propertyLabel;
			},
      getPrice(plainText = false) {
        if (this.listing?.undisclosedPrice) return 'Undisclosed Price';
        if (this.listing?.negotiableRent) return 'Negotiable Rent';

        const { pricing } = this.listing;
        const salePrice = pricing?.sale?.price ?? null;
        const salePriceSqft = pricing?.sale?.sf ?? null;
        const leasePrice = pricing?.lease ?? null;
        const leasePriceMo = pricing?.lease?.mo ?? null;
        const leasePriceSqft = pricing?.lease?.sf ?? null;
        const selectedUnit = pricing?.lease?.selectedUnit ?? null;
        const additionalCosts = leasePrice?.additionalCosts ? ` + ${leasePrice.additionalCosts}` : "";
        const showSecondPrice = this.listing.showSecondaryPrice;

        const formatWithUnit = (price, unit) => this.formatPrice(price) ? `${this.formatPrice(price)}${unit}` : '';
        const rangeFormat = (min, max, unit) => min !== max
            ? `${formatWithUnit(min, '')}-${formatWithUnit(max, unit)}`
            : formatWithUnit(min, unit);

        let firstPrice = selectedUnit === "sqft" ? formatWithUnit(leasePriceSqft?.min, "/sf") : formatWithUnit(leasePriceMo, "/mo");
        let secondPrice = selectedUnit === "sqft" ? formatWithUnit(leasePriceMo, "/mo") : formatWithUnit(leasePriceSqft?.min, "/sf");
        let plainTextPrice = `$${firstPrice || secondPrice}`;

        // Only add the secondary price if it has a valid value
        if (firstPrice && secondPrice && secondPrice !== "$") {
          plainTextPrice += ` ($${secondPrice})`;
        }

        if (plainText) {
          if (salePrice && leasePrice) {
            return `Sale: $${rangeFormat(salePriceSqft?.min, salePriceSqft?.max, "/sf")} (${this.formatPrice(salePrice)}) ; Lease: ${plainTextPrice}${additionalCosts}`;
          }
          return salePrice ? `$${this.formatPrice(salePrice)}` : plainTextPrice + additionalCosts;
        }

        let priceHTML = "";
        if (salePriceSqft) {
          priceHTML += `
      <span class="font-secondary font-bold text-xs uppercase tracking-loose pr-10">Sale:</span>
      $${rangeFormat(salePriceSqft.min, salePriceSqft.max, "/sf")} ($${this.formatPrice(salePrice)})
    `;
          if (leasePrice) {
            priceHTML += `
        <br><span class="font-secondary font-bold text-xs text-gray400 uppercase tracking-loose">Lease:</span>
        <span class="text-gray400 font-secondary font-semibold text-base leading-loose pl-10">${plainTextPrice}${additionalCosts}</span>
      `;
          }
        } else if (salePrice || leasePrice) {
          priceHTML += salePrice ? `$${this.formatPrice(salePrice)}` : plainTextPrice;
          if (leasePrice && firstPrice && secondPrice && showSecondPrice) {
            priceHTML += `
        <br><span class="text-gray400 font-secondary font-semibold text-base leading-loose pl-10">$${secondPrice}${additionalCosts}</span>
      `;
          }
        }

        return priceHTML;
      },



      getPackagePrice() {
        const { pricing } = this.listing.package;
        const salePrice = pricing?.sale?.price ?? null;
        const leasePriceMo = pricing?.lease?.mo ?? null;
        const leasePriceSqft = pricing?.lease?.sf?.min ?? null;
        const selectedUnit = pricing?.lease?.selectedUnit ?? null;

        return salePrice
            ? `$${this.formatPrice(salePrice)}`
            : selectedUnit === "sqft"
                ? `$${this.formatPrice(leasePriceSqft)}/sf`
                : `$${this.formatPrice(leasePriceMo)}/mo`;
      },

      getPropertyPrice(property) {
        const { pricing } = property;
        const salePrice = pricing?.sale?.price ?? null;
        const leasePriceMo = pricing?.lease?.mo ?? null;
        const leasePriceSqft = pricing?.lease?.sf?.min ?? null;
        const selectedUnit = pricing?.lease?.selectedUnit ?? null;

        const formatWithUnit = (price, unit) => (this.formatPrice(price) ? `${this.formatPrice(price)}${unit}` : '');

        if (salePrice && this.listing.entryType === "package") {
          return `$${this.formatPrice(salePrice)}`;
        }
        if (pricing.lease && this.listing.entryType === "combinedSpace") {
          return selectedUnit === "mo"
              ? `$${formatWithUnit(leasePriceMo, "/mo")}`
              : `$${formatWithUnit(leasePriceSqft, "/sf")}`;
        }

        return salePrice ? `$${this.formatPrice(salePrice)}` : `$${selectedUnit === "mo" ? formatWithUnit(leasePriceMo, "/mo") : formatWithUnit(leasePriceSqft, "/sf")}`;
      },

      formatPrice(value) {
        const num = Number(value);
        if (isNaN(num)) return '';
        return addCommas(num.toFixed(2).replace('.00', ''));
      },

      getStreet(listing) {
				const { street1, street2 } = listing.address;
				return street1 + (street2 ? ", " + street2 : "");
			},
			getCityStateZip(listing) {
				const { city, state, zip } = listing.address;
				return `${city}, ${stateToAbbreviation(state) || state} ${zip}`;
			},
			getSf(listing) {
				let sf = listing.sf;
				let sfWithCommas = "";
				if (sf.min != sf.max) {
					sfWithCommas = addCommas(sf.min) + "-" + addCommas(sf.max);
				} else {
					sfWithCommas = addCommas(sf.min);
				}
				sfWithCommas += listing.entryType != "property" ? " total" : "";
				sfWithCommas += " sf";

				if (sf.min) {
					return sfWithCommas;
				}

				return "";
			},
			getAcres(listing) {
				let acres = listing.acres;
				let acresWithCommas = addCommas(acres);
				acresWithCommas += listing.entryType != "property" ? " total" : "";
				acresWithCommas += " acres";

				if (acres) {
					return acresWithCommas;
				}

				return "";
			},
			openGallery(index = 0) {
				if (this.listing.video && index > 4) {
					index--;
				}
				this.galleryIndex = index;
				this.showGallery = true;
			},
			closeGallery() {
				this.showGallery = false;
			},
			prevPhoto() {
				if (this.galleryIndex > 0) {
					this.galleryIndex -= 1;
				} else {
					this.galleryIndex = this.listing.photos.length - 1;
				}
			},
			nextPhoto() {
				if (this.galleryIndex < this.listing.photos.length - 1) {
					this.galleryIndex += 1;
				} else {
					this.galleryIndex = 0;
				}
			},
			getSlideSize(index, slideIndex) {
				let onLastSlide = slideIndex == this.galleryRange.length - 1;
				let modulus = this.photos.length % 4;
				index++;

				if (onLastSlide && modulus == 1 && index == 1) {
					return "w-full h-full";
				} else if (
					onLastSlide &&
					(modulus == 2 || (modulus == 3 && index == 1))
				) {
					return "w-full h-1/2";
				} else {
					return "w-1/2 h-1/2";
				}
			},
			createMap() {
				if (!window.mapboxgl) {
					this.mapUnavailable = true;
					return null;
				}
				var vm = this;

				window.mapboxgl.accessToken =
					"pk.eyJ1Ijoic3RlYWRmYXN0ZGVzaWduZmlybSIsImEiOiJjanA4cjJua3gyMms4M3FwN2h2dGtpY2FsIn0.WNrUU--j6DaF_aV6PiTrcA";

				try {
					this.map = new window.mapboxgl.Map({
						container: "map",
						style: "mapbox://styles/mapbox/" + this.mapStyle,
						scrollZoom: false,
						zoomControl: false,
						zoom: 10
					});
				} catch (error) {
					console.warn("Mapbox failed to initialize.", error);
					this.mapUnavailable = true;
					return null;
				}

				this.mapUnavailable = false;

				var locations = {};

				if (vm.listing.entryType == "property") {
					locations = {
						type: "Locations",
						features: [
							{
								geometry: {
									type: "Point",
									coordinates: [
										this.listing.address.lng,
										this.listing.address.lat
									]
								},
								listing: this.listing
							}
						]
					};
				} else {
					let features = [];

					if (vm.listing.properties) {
						vm.listing.properties.forEach(property => {
							features.push({
								geometry: {
									type: "Point",
									coordinates: [
										property.address.lng,
										property.address.lat
									]
								},
								listing: property
							});
						});
					}

					locations = {
						type: "Locations",
						features: features
					};
				}

				if (!locations.features.length) {
					this.mapUnavailable = true;
					return null;
				}

				// locations.features.forEach(marker => {
				// 	var listing = marker.listing

				// 	// create a DOM element for the marker
				// 	var el = document.createElement('div');
				// 	// el.className = 'map-marker icon-s-single-family';

				// 	el.className = 'map-marker ' + vm.types[listing.propertyType.value].color.slice(5, -3) + ' icon-s-' + vm.types[listing.propertyType.value].icon;

				// 	// add marker to map
				// 	new mapboxgl.Marker(el)
				// 		.setLngLat(marker.geometry.coordinates)
				// 		.setOffset([0, -18])
				// 		.addTo(this.map);
				// });

				locations.features.forEach(function(marker) {
					// create a DOM element for the marker
					var el = document.createElement("div");
					// el.className = 'map-marker icon-s-single-family';
					var listing = marker.listing;
					var listingURL = listing.url;
					var details = "";

					if (listing.sf && listing.sf.min > 0 && listing.sf.max > 0) {
						details += `
						<div class="flex px-10">
							<span class="icon-m-measure text-gray300"></span>
							<div class="listing-info-item ml-10">${
								listing.sf.min != listing.sf.max
									? addCommas(listing.sf.min) +
									  "-" +
									  addCommas(listing.sf.max)
									: addCommas(listing.sf.min)
							} sf</div>
						</div>`;
					}

					if (listing.acres > 0) {
						details += `
						<div class="flex px-10">
							<span class="icon-m-tree text-gray300"></span>
							<div class="listing-info-item ml-10">${addCommas(listing.acres)} acres</div>
						</div>`;
					}

					if (listing.package) {
						details +=
							`
						<div class="flex px-10 ml-auto">` +
							(listing.settings.inPackageOnly
								? `
								<span class="icon-m-package-only text-gray300"></span>`
								: `
								<span class="icon-m-package-included text-gray300"></span>`) +
							`
						</div>`;
					}

					el.className =
						"map-marker " +
						vm.types[listing.propertyType.value].color.slice(5, -3) +
						" icon-s-" +
						vm.types[listing.propertyType.value].icon;

					// var propertyLabel = vm.propertyLabel(listing)

					// add marker to map
					new window.mapboxgl.Marker(el)
						.setLngLat(marker.geometry.coordinates)
						.setPopup(
							new window.mapboxgl.Popup({ offset: 0 }) // add popups
								.setHTML(
									`
							<div class="listing-item" style="width: 300px;">` +
										(listing.photos.length > 0
											? `<a class="listing-item-header cursor-pointer" href="${listingURL}" style="z-index: initial;">
									<span class="bg-blue200 cursor-pointer absolute pin bg-cover bg-center" style="background-image: url(${listing.photos[0].thumb}); z-index: -1;"
									></span>
								</a>`
											: "") +
										`<div class="listing-item-details-wrapper" style="padding: 0px">
									<a class="listing-item-details cursor-pointer" style="margin-top: 0px; border-radius: 0px;" href="${listingURL}">
										<div class="listing-item-type" style="top: 0px; left: 30px;">
											<div class="absolute pin opacity-40 rounded-b ${vm.types[
												listing.propertyType.value
											].color.replace("text-", "bg-")}"></div>
											<div class="relative">${vm.getPropertyLabel(listing)}</div>
										</div>

										<div class="listing-item-details-top" style="padding: 25px; margin-top: 0px">
											<div class="flex items-center ${listing.propertyType.value}">
												<span class="-ml-3 listing-item-icon ${vm.iconClasses(listing)}"></span>` +
										(listing.settings.inPackageOnly
											? `<ListingIcon :color="${vm.types["package"].color}" icon="package-only" class="ml-10" />`
											: "") +
										(listing.settings.inPackageOnly &&
										listing.package
											? `<div class="ml-15 text-gray700 font-bold font-secondary uppercase tracking-loose text-tiny bg-gray50 rounded py-5 px-8">In ${listing.package.type.label} Only</div>`
											: `<div class="ml-15 text-xl text-gray700 font-secondary font-bold">${vm.getPropertyPrice(
													listing
											  )}</div>`) +
										`</div>

											<div class="mt-20">
												<div class="font-bold text-gray700">${vm.getStreet(listing)}</div>
												<div class="mt-10 text-sm">${vm.getCityStateZip(listing)}</div>
											</div>

										</div>
										<div class="border-t border-solid border-gray100">
											<div class="-mx-10 p-25 flex">
												${details}
											</div>
										</div>
									</a>
								</div>
							</div>
							`
								)
						)
						.setOffset([0, -18])
						.addTo(vm.map);
				});

				var bounds = new window.mapboxgl.LngLatBounds();

				locations.features.forEach(function(feature) {
					bounds.extend(feature.geometry.coordinates);
				});

				this.map.fitBounds(bounds, {
					padding: 100,
					maxZoom: 13,
					animate: false
				});
				// map.addControl(new mapboxgl.NavigationControl(), 'top-left');
			}
		},
		computed: {
			listingColor() {
				let propertyType = this.listing.propertyType.value;
				return this.types[propertyType].color;
			},
			listingIcon() {
				let propertyType = this.listing.propertyType.value;
				return this.types[propertyType].icon;
			},
			description() {
				let description = this.listing.description;
				description = description.replace(/<p>/g, '<p class="mb-30">');
				description = description.replace(
					/<ul>/g,
					'<ul class="features no-columns mb-30">'
				);
				return description;
			},
			galleryRange() {
				if (this.photos.length) {
					return _.range(Math.ceil(this.photos.length / 4));
				}
			},
			photos() {
				var photos = [...this.listing.photos];
				photos.shift();
				if (this.listing.video) {
					photos.splice(3, 0, this.listing.photos[0]);
				}
				return photos;
			},
			perPage() {
				if (
					!this.photos.length ||
					!["md", "lg", "xl"].includes(this.windowSize)
				) {
					return 1;
				} else {
					return 2;
				}
			},
			agentNames() {
				let brokers = Object.values(this.listing.brokers);
				if (brokers.length == 0) {
					return "Request a Showing";
				} else if (brokers.length == 1) {
					return brokers[0].name;
				} else if (brokers.length > 1) {
					let names = [];
					brokers.forEach(agent => {
						names.push(agent.name);
					});
					return [names.slice(0, -1).join(", "), names.slice(-1)[0]].join(
						" & "
					);
				}
			},
			showingPhone() {
				let brokers = Object.values(this.listing.brokers);
				if (brokers.length == 1) {
					return brokers[0].phone;
				} else {
					return this.listing.contact.phone;
				}
			},
			showingEmail() {
				let emailLink = "";
				emailLink += this.listing.contact.email;

				emailLink +=
					"?subject=" +
					encodeURIComponent(
						"Showing Request: " +
							this.getStreet(this.listing) +
							", " +
							this.getCityStateZip(this.listing)
					);


				let agentEmails = [];
				this.listing.brokers.forEach(agent => {
					if (agent.email) agentEmails.push(agent.email);
				})

				if (agentEmails.length) {
					emailLink += "&cc=" + agentEmails.join(",");
				}

				return emailLink;
			},
			propertyDetails() {
				const { listing, listingColor, getSf, getAcres } = this;
				const labels = {
					"Best Use": listing.usage.best,
					...(listing.entryType == "property" && {
						SF: getSf(listing).replace("total ", "")
					}),
					...(listing.entryType == "property" && {
						Acres: getAcres(listing).replace("total ", "")
					}),
					...(listing.entryType != "property" && {
						"Total SF": getSf(listing).replace("total ", "")
					}),
					...(listing.entryType != "property" && {
						"Individual SF": listing.properties
					}),
					...(listing.entryType != "property" && {
						"Total Acres": getAcres(listing).replace("total ", "")
					}),
					...(listing.entryType != "property" && {
						"Individual Acres": listing.properties
					}),
					"Building Year": listing.misc.buildingYear,
					"Building Description": listing.misc.buildingDescription,
					Zoning: listing.misc.zoning,
					"Permitted Use": listing.usage.permitted,
					"Potential Use": listing.usage.potential,
					Attachments: listing.attachments
				};
				var propertyDetails = {};

				Object.keys(labels).forEach(label => {
					let value = labels[label];

					if (label == "Attachments") {
						const links = value
							.map(attachment => {
								return `<a href="${
									attachment.url
								}" class="link link-${listingColor.slice(
									5,
									-3
								)}" target="_blank">${attachment.title}</a>`;
							})
							.filter(n => n);
						if (links.length)
							propertyDetails[label] =
								"<div>" + links.join(", ") + "</div>";
					} else if (label == "Individual SF") {
						const sf = value
							.map(property => {
								if (property.sf.min) {
									return addCommas(property.sf.min) + " sf";
								}
							})
							.filter(n => n);
						if (sf.length > 1)
							propertyDetails[label] =
								"<div>" + sf.join("; ") + "</div>";
					} else if (label == "Individual Acres") {
						const acres = value
							.map(property => {
								if (property.acres) {
									return addCommas(property.acres) + " acres";
								}
							})
							.filter(n => n);
						if (acres.length > 1)
							propertyDetails[label] =
								"<div>" + acres.join("; ") + "</div>";
					} else if (Array.isArray(value)) {
						if (value.length) propertyDetails[label] = value.join(", ");
					} else if (value) {
						propertyDetails[label] = value;
					}
				});

				return propertyDetails;
			},
			listingDetails() {
				const { listing, listingColor } = this;
				const labels = {
					...(!listing.settings.inPackageOnly && {
						'List Price': this.getPrice(true)
					}),
					...(listing.legacy.analysis && listing.status.value == 'closed' &&{
						Analysis: '<div>' + listing.legacy.analysis + '</div>'
					}),
					...(listing.package && {
						[listing.package.type.label]: listing.package.url
					}),
					"Cap Rate": listing.misc.capRate,
					...(listing.entryType == "property" && {
						Address:
							this.getStreet(listing) +
							", " +
							this.getCityStateZip(listing)
					}),
					...(listing.entryType != "property" && {
						Addresses: listing.properties
					}),
					City: Object.values(listing.city),
					County: Object.values(listing.county),
					"Parcel #": listing.misc.parcelNo,
					"Listing Agent(s)": Object.values(listing.brokers),
					Status: listing.status.label,
					...(listing.status.value == "closed" && {
						"Closed Date": this.dateClosed
					}),
					"Data Source": listing.misc.dataSource,
					"Listing ID": listing.objectID,
					"MLS#": listing.misc.mlsNo
				};
				var listingDetails = {};

				Object.keys(labels).forEach(label => {
					let value = labels[label];

					if (label == "Listing Agent(s)") {
						const agents = value.map(agent => {
							return `<a href="${
								agent.url
							}" class="link link-${listingColor.slice(5, -3)}">${
								agent.name
							}</a>`;
						});
						listingDetails[label] =
							"<div>" + agents.join(", ") + "</div>";
					} else if (label == "Addresses") {
						const addresses = value
							.map(property => {
								return (
									this.getStreet(property) +
									", " +
									this.getCityStateZip(property)
								);
							})
							.filter(n => n);
						if (addresses.length)
							listingDetails[label] =
								"<div>" + addresses.join("<br>") + "</div>";
					} else if (label == "City" || label == "County") {
						if (value.length > 1) {
							label = label
								.replace("City", "Cities")
								.replace("County", "Counties");
						}
						listingDetails[label] = value
							.map(principality => {
								return principality.name;
							})
							.join(", ");
					} else if (label == "Package" || label == "Combined Space") {
						listingDetails[
							label
						] = `<a href="${value}" class="link link-${listingColor.slice(
							5,
							-3
						)}">${value.replace("/properties/", "")}</a>`;
					} else if (Array.isArray(value)) {
						if (value.length) listingDetails[label] = value.join(", ");
					} else if (value) {
						listingDetails[label] = value;
					}
				});

				return listingDetails;
			},
			legacyDetails() {
				const { listing, listingColor } = this;
				const labels = {
					Analysis: listing.legacy.analysis,
					"Grantee/Tenant": listing.legacy.granteeTenant,
					"Grantor/Landlord": listing.legacy.grantorLandlord,
					"# of Multifamily Units": listing.legacy.noMultifamilyUnits,
					"Use at Time of Sale": listing.legacy.useAtTimeOfSale,
					Utilities: listing.legacy.utilities
				};
				var listingDetails = {};

				Object.keys(labels).forEach(label => {
					let value = labels[label];

					if (label == "Analysis" && value) {
						listingDetails[label] =
							'<div class="w-full">' + value + "</div>";
					} else if (Array.isArray(value)) {
						if (value.length) listingDetails[label] = value.join(", ");
					} else if (value) {
						listingDetails[label] = value;
					}
				});

				return listingDetails;
			},
			dateClosed() {
				let date = new Date(this.listing.dateClosed * 1000);
				return date.toLocaleDateString("en-US");
			},
			notes() {
				let notes = this.listing.notes;
				notes = notes.replace(/<p>/gi, '<p class="mb-30">');
				notes = notes.replace(/<ul>/gi, '<ul class="bullets">');
				return notes;
			}
		},
		created() {
            let id = window.location.pathname.split("/").pop().split('?')[0];
			this.getListing(id);
		},
		mounted() {},
		beforeUnmount() {
			if (this.map) {
				this.map.remove();
				this.map = null;
			}
		},
		watch: {
			listing(listing) {
				// if (!listing || Object.keys(listing).length == 0) return false;
				// const $heroBg = document.querySelector('#hero-background-image');
				// $heroBg.style.backgroundImage = `url('${this.listing.photos[0]}')`;
				// setTimeout(() => {console.log('worked'); this.createMap()}, 1000);
			}
		},
		components: {
			ListingIcon,
			ListingInformationSection,
			ListingGallery,
			LeadCapturing,
			Carousel,
			Slide,
			spinner
		}
	};
</script>

<style>
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.fade-enter-to,
	.fade-leave-from {
		opacity: 1;
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.25s ease;
	}
</style>
